import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/local.js';
import { acknowledgeIntake, listIntakesForDay, skipIntake } from '../db/intakes.js';

/**
 * Die Seite nach dem Antippen einer Benachrichtigung.
 *
 * **Warum eine eigene Seite und nicht die hervorgehobene Zeile in der Liste:**
 * Auf iOS gibt es keine Aktions-Knöpfe in Benachrichtigungen (am Gerät geprüft),
 * Antippen ist also der einzige Weg. Wer tippt, hat sich schon entschieden, sich
 * damit zu befassen — und tut das um acht Uhr morgens, einhändig, halb wach. Eine
 * Entscheidung, zwei große Flächen, kein Scrollen, keine Nachbarknöpfe zum
 * Vertippen.
 *
 * Die dritte Möglichkeit ist genauso wichtig: **nachschauen ohne zu entscheiden.**
 * Wer nur wissen wollte, was ansteht, darf nicht gezwungen werden, etwas zu
 * behaupten.
 */
export function AcknowledgeScreen({
  intakeId,
  onDone,
}: {
  intakeId: string;
  onDone: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  const intake = useLiveQuery(
    async () => (await listIntakesForDay(new Date(), db)).find((entry) => entry.id === intakeId),
    [intakeId],
    undefined,
  );

  const zeit = new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' });
  const erledigt = intake?.acknowledgedAt ?? null;
  const uebersprungen = intake?.skippedAt ?? null;

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center gap-8 px-4">
      <div className="text-center">
        <p className="kw-kicker">
          {intake ? t('acknowledge.dueAt', { time: zeit.format(intake.dueAt) }) : ''}
        </p>
        <h1 className="kw-figure mt-2 text-4xl">
          {intake?.supplementName ?? t('acknowledge.unknown')}
        </h1>
        {intake?.dose ? <p className="mt-1 text-lg text-ink-muted">{intake.dose}</p> : null}
      </div>

      {erledigt || uebersprungen ? (
        <div className="text-center">
          <p className={erledigt ? 'text-good' : 'text-warn'}>
            {erledigt
              ? t('acknowledge.alreadyTaken', { time: zeit.format(erledigt) })
              : t('acknowledge.alreadySkipped', { time: zeit.format(uebersprungen!) })}
          </p>
        </div>
      ) : (
        // Zwei Flächen, kein Scrollen, keine Nachbarknöpfe: Das hier wird um acht
        // Uhr morgens einhändig und halb wach bedient. 6rem sind mehr als die
        // sonstige Daumenfläche, weil danebengreifen hier eine falsche Aussage
        // in die Datenbank schreibt.
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => void acknowledgeIntake(intakeId).then(onDone)}
            className="kw-btn min-h-24 rounded-3xl text-2xl"
          >
            {t('today.take')}
          </button>
          <button
            type="button"
            onClick={() => void skipIntake(intakeId, null).then(onDone)}
            className="kw-btn kw-btn-quiet min-h-24 rounded-3xl text-2xl hover:border-warn hover:text-warn"
          >
            {t('today.skip')}
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={onDone}
        className="mx-auto text-sm text-ink-muted underline underline-offset-4"
      >
        {erledigt || uebersprungen ? t('acknowledge.continue') : t('acknowledge.later')}
      </button>
    </main>
  );
}
